import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { notifySkippedByGlobal } from "@/lib/prohibited-keyword-notices";
import type { BulkAddProhibitedKeywordsResponse } from "@/types/prohibited-keyword";
import { useAppData } from "@/contexts/AppDataContext";
import { useGetAccount } from "@/resources/account/account.api";
import { ACCOUNT_ROLE } from "@/constants/account";
import {
  useAddGlobalProhibitedKeyword,
  useBulkAddGlobalProhibitedKeywords,
} from "@/resources/global-prohibited-keyword/global-prohibited-keyword.api";
import {
  useAddProhibitedKeyword,
  useBulkAddProhibitedKeywords,
} from "@/resources/prohibited-keyword/prohibited-keyword.api";
import {
  useAddProductProhibitedKeyword,
  useBulkAddProductProhibitedKeywords,
} from "@/resources/product-prohibited-keyword/product-prohibited-keyword.api";
import { ProhibitedKeywordTierPicker, type ProhibitedKeywordTier } from "./ProhibitedKeywordTierPicker";
import { ProhibitedKeywordAddForm } from "./ProhibitedKeywordAddForm";

function parseKeywordList(text: string): string[] {
  return text
    .split(/[,\n]+/)
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean);
}

interface AddProhibitedKeywordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyword: string;
  brandId: number | null;
  productId: number | null;
  productName?: string;
}

// AUT-41 Part 3: from a Search Terms double-click, let the user add the keyword to
// any tier of the Universal -> Brand -> Product chain they have access to. Picking a
// tier lands on an editable Single/Bulk/File Upload form (prefilled with the
// double-clicked word) rather than committing immediately, matching the prototype --
// the double-click just seeds the starting point, it doesn't have to be the final input.
// Owns state and mutation dispatch; ProhibitedKeywordTierPicker and
// ProhibitedKeywordAddForm are the two presentational screens.
export function AddProhibitedKeywordDialog({
  open,
  onOpenChange,
  keyword,
  brandId,
  productId,
  productName,
}: AddProhibitedKeywordDialogProps) {
  const { brands } = useAppData();
  const { data: account } = useGetAccount();
  const isAdmin =
    account?.role === ACCOUNT_ROLE.OWNER || account?.role === ACCOUNT_ROLE.ADMIN;

  const brandName = brands.find((b) => b.id === brandId)?.name ?? "this brand";

  const addGlobal = useAddGlobalProhibitedKeyword();
  const bulkAddGlobal = useBulkAddGlobalProhibitedKeywords();
  const addBrand = useAddProhibitedKeyword();
  const bulkAddBrand = useBulkAddProhibitedKeywords();
  const addProduct = useAddProductProhibitedKeyword();
  const bulkAddProduct = useBulkAddProductProhibitedKeywords();

  const [tier, setTier] = useState<ProhibitedKeywordTier | null>(null);
  const [single, setSingle] = useState("");
  const [bulk, setBulk] = useState("");

  useEffect(() => {
    if (!open) {
      setTier(null);
      setSingle("");
      setBulk("");
    } else {
      setSingle(keyword.trim().toLowerCase());
    }
  }, [open, keyword]);

  const tierLabel =
    tier === "global"
      ? "Global"
      : tier === "brand"
        ? `Brand · ${brandName}`
        : tier === "product"
          ? `Product · ${productName ?? "this product"}`
          : "";

  const isPending =
    addGlobal.isPending ||
    bulkAddGlobal.isPending ||
    addBrand.isPending ||
    bulkAddBrand.isPending ||
    addProduct.isPending ||
    bulkAddProduct.isPending;

  const reportBulkResult = (
    inserted: number,
    skipped: number,
    clear: () => void,
    skippedGlobal?: string[],
  ) => {
    if (tier === "brand" || tier === "product") {
      notifySkippedByGlobal(skippedGlobal, tier === "brand" ? "Brand" : "Product");
    }
    if (inserted === 0) {
      toast({
        title: "No new keywords",
        description: skipped > 0 ? "All keywords are already in the list." : "Enter at least one keyword.",
      });
      return;
    }
    toast({
      title: "Added",
      description: `${inserted} keyword${inserted !== 1 ? "s" : ""} added to ${tierLabel}${
        skipped ? `, ${skipped} duplicate${skipped !== 1 ? "s" : ""} skipped` : ""
      }.`,
    });
    clear();
    onOpenChange(false);
  };

  const handleAddSingle = () => {
    const trimmed = single.trim().toLowerCase();
    if (!trimmed || !tier) return;

    const onSuccess = () => {
      toast({ title: "Added", description: `"${trimmed}" added to ${tierLabel} prohibited keywords.` });
      setSingle("");
      onOpenChange(false);
    };

    if (tier === "global") addGlobal.mutate({ keyword: trimmed, origin: "manual" }, { onSuccess });
    else if (tier === "brand" && brandId)
      addBrand.mutate({ brand_id: brandId, keyword: trimmed, origin: "manual" }, { onSuccess });
    else if (tier === "product" && productId)
      addProduct.mutate({ product_id: productId, keyword: trimmed, origin: "manual" }, { onSuccess });
  };

  const bulkAddForTier = (
    parsed: string[],
    onSuccess: (result: BulkAddProhibitedKeywordsResponse) => void,
  ) => {
    if (tier === "global") bulkAddGlobal.mutate({ keywords: parsed, origin: "manual" }, { onSuccess });
    else if (tier === "brand" && brandId)
      bulkAddBrand.mutate({ brand_id: brandId, keywords: parsed, origin: "manual" }, { onSuccess });
    else if (tier === "product" && productId)
      bulkAddProduct.mutate({ product_id: productId, keywords: parsed, origin: "manual" }, { onSuccess });
  };

  const handleBulkAdd = () => {
    const parsed = parseKeywordList(bulk);
    if (parsed.length === 0 || !tier) return;

    bulkAddForTier(parsed, (result) => reportBulkResult(result.inserted, result.skipped, () => setBulk(""), result.skipped_global));
  };

  const handleFileUpload = (file: File) => {
    if (!tier) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseKeywordList(text);
      if (parsed.length === 0) return;

      bulkAddForTier(parsed, (result) => reportBulkResult(result.inserted, result.skipped, () => undefined, result.skipped_global));
    };
    reader.readAsText(file);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tier ? `Add ${tierLabel} Keyword` : "Add a Prohibited Keyword"}</DialogTitle>
          {!tier && (
            <DialogDescription>
              Choose which scope this keyword should apply to. Each scope inherits from the ones above it.
            </DialogDescription>
          )}
        </DialogHeader>

        {!tier ? (
          <ProhibitedKeywordTierPicker
            isAdmin={isAdmin}
            brandId={brandId}
            brandName={brandName}
            productId={productId}
            productName={productName}
            onSelect={setTier}
          />
        ) : (
          <ProhibitedKeywordAddForm
            single={single}
            onSingleChange={setSingle}
            onAddSingle={handleAddSingle}
            bulk={bulk}
            onBulkChange={setBulk}
            onBulkAdd={handleBulkAdd}
            onFileUpload={handleFileUpload}
            onChangeScope={() => setTier(null)}
            isPending={isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
